import denormalizeTaxNumber from "denormalize-steuernummer";
import React, { useCallback } from "react";
import moment from "moment";
import { Input, Form, Select, DatePicker } from "antd";
import type { FormInstance } from "antd";
import { useTranslation } from "react-i18next";

import { TaxNumber } from "../../../../../../api/graphql";
import { StateAbbreviation } from "./types";
import {
  getTaxNumberError,
  getTaxNumberErrorMessage,
  isTaxNumberWarning,
  NEW_TAX_NUMBER_RECORD_ID,
  STATE_OPTION,
  TAX_NUMBER_OPTIONS,
  TABLE_COLUMN_TITLES,
  IS_MAIN_TAX_NUMBER_OPTIONS,
} from "./utils";

export interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  record: TaxNumber;
  index: number;
  required: boolean;
  children: React.ReactNode;
}

const EditableRow: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  record,
  index,
  required,
  children,
  ...restProps
}) => {
  let inputNode;
  const { t } = useTranslation("TaxNumbers");
  // The number cannot change after creation, so only a new row is validated.
  const isNewRecord = record?.id === NEW_TAX_NUMBER_RECORD_ID;
  const isTaxNumberField = title === TABLE_COLUMN_TITLES.TAX_NUMBER;

  const getInitialValue = useCallback((title: string, record: TaxNumber) => {
    const { taxNumber, taxState } =
      denormalizeTaxNumber(record.taxNumber)! || {};
    switch (title) {
      case TABLE_COLUMN_TITLES.TYPE:
        return record.type;
      case TABLE_COLUMN_TITLES.STATE:
        return taxState || StateAbbreviation.DE_BE;
      case TABLE_COLUMN_TITLES.TAX_NUMBER:
        return taxNumber;
      case TABLE_COLUMN_TITLES.VALID_FROM:
        return record.validFrom && moment(record.validFrom);
      case TABLE_COLUMN_TITLES.DESCRIPTION:
        return record.description;
      case TABLE_COLUMN_TITLES.MAIN_TAX_NUMBER:
        return record.isMainBusinessTaxNumber;

      default:
        return null;
    }
  }, []);

  switch (title) {
    case TABLE_COLUMN_TITLES.TYPE:
      inputNode = (
        <Select style={{ width: 100 }} options={TAX_NUMBER_OPTIONS} />
      );
      break;
    case TABLE_COLUMN_TITLES.STATE:
      inputNode = <Select style={{ width: 100 }} options={STATE_OPTION} />;
      break;
    case TABLE_COLUMN_TITLES.VALID_FROM:
      inputNode = <DatePicker style={{ width: "115px" }} />;
      break;
    case TABLE_COLUMN_TITLES.TAX_NUMBER:
      inputNode = <Input style={{ width: "135px" }} />;
      break;
    case TABLE_COLUMN_TITLES.MAIN_TAX_NUMBER:
      inputNode = (
        <Select style={{ width: 120 }} options={IS_MAIN_TAX_NUMBER_OPTIONS} />
      );
      break;
    default:
      inputNode = <Input style={{ width: "90px" }} />;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          initialValue={getInitialValue(title, record)}
          dependencies={isTaxNumberField && isNewRecord ? ["state"] : undefined}
          rules={[
            { required, message: String(t("errors.required", { title })) },
            ...(isTaxNumberField && isNewRecord
              ? [
                  ({ getFieldValue }: Pick<FormInstance, "getFieldValue">) => ({
                    validator: (_: unknown, value?: string) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      const state = getFieldValue("state") as StateAbbreviation;
                      const error = getTaxNumberError(value, state);
                      // NO_TAX_OFFICE and AMBIGUOUS_STATE are only warnings, confirmed on save.
                      if (error && !isTaxNumberWarning(error)) {
                        return Promise.reject(
                          new Error(getTaxNumberErrorMessage(t, error, state))
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]
              : []),
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export default EditableRow;
